import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AuthContext from "../auth/AuthContext";
import Post from "../components/Post";
import PostFormDialog from "../components/PostFormDialog";
import GroundingEditDialog from "../components/grounding/GroundingEditDialog";
import CommentsSection from "../components/CommentsSection";
import { deletePost, getPost, updatePost } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function PostDetail() {
  const params = useParams();
  const postRef = params.postRef || params["*"] || "";
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const ref = decodeURIComponent(postRef);
  const isGrounding = post?.composition === "grounding";
  const backTo = isGrounding ? "/grounding" : "/";
  const backLabel = isGrounding ? "← Refleksion-journal" : "← Hjem";
  const isAdmin = !!user?.is_admin;
  const canFullEdit = isAdmin;

  useDocumentTitle(post?.title || "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ref) {
        setLoading(false);
        setError("Post not found");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await getPost(ref);
        if (!cancelled) setPost(data);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === "string" ? detail : err.message || "Failed to load post");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref]);

  const handleEditSave = async (payload) => {
    const updated = await updatePost(post.permalink || post.post_uuid, payload);
    setEditOpen(false);
    if (updated.permalink && updated.permalink !== post.permalink) {
      navigate(`/posts/${updated.permalink}`, { replace: true });
    } else {
      setPost(updated);
    }
  };

  const handleEditDelete = async () => {
    await deletePost(post.permalink || post.post_uuid);
    setEditOpen(false);
    navigate(isGrounding ? "/grounding" : "/");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button component={RouterLink} to={backTo} variant="text">
          {backLabel}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mt: 2 }}
      >
        <Button component={RouterLink} to={backTo} variant="text" sx={{ px: 0, alignSelf: "flex-start" }}>
          {backLabel}
        </Button>
      </Stack>

      <Post
        post={post}
        variant="detail"
        onEdit={canFullEdit ? () => setEditOpen(true) : undefined}
      />

      <CommentsSection kind="post" contentRef={post?.permalink || post?.post_uuid} />

      {isGrounding ? (
        <GroundingEditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSubmit={handleEditSave}
          onDelete={handleEditDelete}
          submitLabel="Gem"
          initial={{
            title: post?.title || "",
            body: post?.body || "",
            tags: post?.tags || [],
          }}
        />
      ) : (
        <PostFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSubmit={handleEditSave}
          onDelete={handleEditDelete}
          title="Rediger indlæg"
          submitLabel="Gem"
          formKey={post?.post_uuid || "edit"}
          postUuid={post?.post_uuid}
          postType="post"
          hideTemplate={false}
          initial={{
            title: post?.title || "",
            body: post?.body || "",
            photos: post?.photos || [],
            tastings: post?.tastings || [],
            visibility: post?.visibility || "private",
            composition: post?.composition || "journal",
            tags: post?.tags || [],
          }}
        />
      )}
    </Box>
  );
}

export default PostDetail;
